/**
 * @name 请求规则
 * @description
 * 1、具备对传参的规则校验，数据类型校验格式等。
 * 2、用于切换dev prod环境的能力，通过vuex的state控制开关, 根据baseUrl比对字典表，返回对应模式的请求baseUrl
 * 3、返回数据的校验规则，200情况下的公用300+词条的错误返回
 * 4、数据访问的url参数追加
 * 5、同类请求通用的规则
 */
import axios from 'axios';
import { findKey, merge } from 'lodash';
import { Message } from 'element-ui';
import store from '@/store';
import defaultSetting from 'settings';
import Schema from 'async-validator';
import { v4 as uuidv4 } from 'uuid';

const { baseURL, present, production, contentType, requestTimeout, messageDuration, successCode, invalidCode, noPermissionCode } = defaultSetting;

class BasicRequest {
    constructor() {
        this.queue = {};
    }

    /**
     * baseURL判定，如果输入与serverMap注册url对比成功，自动切换当前运行环境的baseURL
     * @param {string} url
     */
    getBaseUrl(url) {
        // url与生产环境的url比较，判断key
        const _key = findKey(production, url);
        if (!_key) {
            return '';
        }
        // 在present现在运行的环境，根据key拿到value
        return present[_key];
    }

    async setConfig(config) {
        if (!config.method) {
            return Promise.reject(new Error('config.method is not null!'));
        }
        if (!config.url) {
            return Promise.reject(new Error('config.url is not null!'));
        }
        /**
         * @description
         * 将 {host}/xxx/yyy 的url替换为对应服务的前缀
         * config.url = config.url.replace(/^\{(\w+)\}/, (m, $1) => present[$1] || '');
         * 判断url是否包含{},例如:{node_api}/api/get_xxx
         * 包含则不使用baseURL
         */
        const urlConf = {};
        if (config.baseURL) {
            // `baseURL` 将自动加在 `url` 前面，除非 `url` 是一个绝对 URL。
            // 它可以通过设置一个 `baseURL` 便于为 axios 实例的方法传递相对 URL
            // urlConf.baseURL = config.baseURL || baseURL;
            // baseUrl为正式地址，根据对照表找到当前环境的地址动态映射。
            const _baseURL = this.getBaseUrl(config.baseURL);
            if (!_baseURL) {
                console.warn('配置环境baseURL不存在配置表中', config.baseURL);
            }
            urlConf.baseURL = _baseURL || config.baseURL;
            urlConf.url = config.url;
        } else if (/^\{#(\w+)\}/.test(config.url)) {
            const _baseUrl = config.url.substr(0, config.url.indexOf('/'));
            const _url = config.url.substr(config.url.indexOf('/'));
            urlConf.baseURL = _baseUrl.replace(/^\{#(\w+)\}/, (m, $1) => present[$1] || '');
            urlConf.url = _url;
        } else {
            // present.base为空则使用当前的地址，baseURL()->window.location.origin
            urlConf.baseURL = present.base || baseURL();
            urlConf.url = config.url;
        }
        const _config = {
            ...urlConf,
            timeout: config.timeout || 10000,
            method: config.method || 'GET',
            headers: {
                'content-type': contentType[config.dataType || 'json'],
                traceId: uuidv4(),
                // 'X-Token': getToken(),
            },
            // transformResponse` 在传递给 then/catch 前，允许修改响应数据
            // 根据系统类型判定返回格式的二次转换规范
            // transformResponse: [function (data) {
            //     return data;
            // }]
        };
        return _config;
    }

    propValidator(rules, data) {
        const _vrules = new Promise(function (resolve, reject) {
            // 参考: https://github.com/yiminghe/async-validator
            const _v = new Schema(rules);
            _v.validate(data, (errors, fields) => {
                if (errors) {
                    reject(errors);
                } else {
                    resolve(fields);
                }
            });
        });
        return _vrules;
    }

    /**
     * 《新门户应用开发规范2020-2-2修正版.docx》->4.2.3 返回格式规范
     * 业务遵循http状态码，业务参数异常码包装在data里。
     * 业务参数异常时status为200,
     * 业务参数异常码封装在data里，
     * 异常信息放入stack，
     * msg给开发者看。
     * stack信息只有在调试模式下可见，
     * 参见网关规范（debug模式调试说明）。
     * result = {
     *     status: 101,
     *     msg: 'success',
     *     data: {},
     *     stack: '异常堆栈信息',
     * };
     */
    responseDataRule(data, type) {
        const _rules = [
            {
                status: {
                    type: 'integer',
                    required: true,
                    asyncValidator: (rule, value) => {
                        return new Promise((resolve, reject) => {
                            if (value > 600 && value < 100) {
                                reject(new TypeError('异常码超过规范值的范围，允许范围:1XX/2XX/3XX/4XX/5XX!'));
                            } else {
                                resolve();
                            }
                        });
                    },
                },
                // msg: {
                //     type: 'enum',
                //     enum: ['success', 'fail'],
                //     required: true,
                // },
                // data: {
                //     type: 'object', // is not Array.isArray
                //     required: true,
                // },
                // stack: {
                //     type: 'string', // 可以没有
                // },
            },
            {
                code: {
                    type: 'integer',
                    required: true,
                    asyncValidator: (rule, value) => {
                        return new Promise((resolve, reject) => {
                            if (value > 400 && value < 300) {
                                reject(new TypeError('异常码超过规范值的范围，允许范围:3XX!'));
                            } else {
                                resolve();
                            }
                        });
                    },
                },
            },
        ];
        const _vrules = new Promise(function (resolve, reject) {
            // 参考: https://github.com/yiminghe/async-validator
            const _v = new Schema(_rules[type || 0]);
            _v.validate(data, (errors, fields) => {
                if (errors) {
                    reject(fields);
                } else {
                    resolve();
                }
            });
        });
        return _vrules;
    }

    interceptors(instance, options) {
        // 统计接口/时长/路由
        let startTime = ''
        let endTime = ''
        let action = ''
        // end
        instance.interceptors.request.use(
            async config => {
                // 参数规则校验
                if (config.prop) {
                    // config.data:只适用于这些请求方法 'PUT', 'POST', 和 'PATCH',浏览器专属：FormData, File, Blob
                    // config.params: 是即将与请求一起发送的 URL 参数必须是一个无格式对象(plain object)或 URLSearchParams 对象
                    await this.propValidator(config.prop, config.method !== 'get' ? config.data : config.params);
                }
                // 添加全局的loading...
                // if (!Object.keys(this.queue).length) {
                // Spin.show() 如果存在请求则等待 loading...
                // }
                this.queue[options.url] = true;
                // 发送请求之前做什么？
                if (store.getters.token) {
                    // 让每个请求携带令牌
                    // ['X-Token'] 是自定义 headers key
                    // 请根据请求实际修改
                    // config.headers['X-Token'] = getToken();
                }

                // 统计接口/时长/路由
                startTime = new Date().getTime()
                action = config.url.substring(config.url.indexOf('.com'), config.url.length)
                // end

                return config;
            },
            error => {
                // 处理错误请求
                return Promise.reject(error);
            },
        );
        instance.interceptors.response.use(
            /**
             * 如果你想获取 headers or status
             * 请返回  response => response
             * 通过自定义代码确定请求状态
             * 请参考用例
             * 可以通过HTTP状态码来判断状态
             */
            async response => {
                const { data } = response;
                /**
                 * 《新门户应用开发规范2020-2-2修正版.docx》->4.2.3 返回格式规范
                 */
                if (data) {
                    await this.responseDataRule(data);
                } else {
                    return Promise.reject(new Error('请求没有返回数据'));
                }
                // 如果不等于(默认:200或0)，则判断为网管错误根据词条显示
                // status不在正确的successCode代码中
                if (!successCode.includes(Number(data.status))) {
                    /**
                     * @description 兼容国际化词条问题
                     * @todo 国际化问题
                     */
                    Message({
                        message: data.stack || 'Error',
                        type: 'error',
                        duration: messageDuration,
                    });
                    return Promise.reject(new Error('请求状态错误, 非200请求，或数据类型错误!'));
                }
                // 状态正常的数据
                //const { data: result } = response.data;
                const result = response.data;
                /**
                 * @description 业务参数异常时status为200,业务参数异常码封装在data里，
                 * @todo 暂定code为错误码
                 */
                if (result && result.code) {
                    await this.responseDataRule(result, 1);
                    // 有错误吗则说明，需要对业务错误的内容按照词条提示用户错误
                    /**
                     * @todo 国际化问题
                     */
                    Message({
                        message: '业务系统错误!', // 兼容国际化词条问题
                        type: 'error',
                        duration: messageDuration,
                    });
                    // 登录失效code, 默认302
                    if (invalidCode === result.code) {
                        console.warn('登录失效code');
                    }
                    // 无权限code, 默认301
                    if (noPermissionCode === result.code) {
                        console.warn('无权限code');
                    }
                    // 假设: 300:非法令牌；312:其他客户端登录；314:令牌过期；
                    if (result.code === 300 || result.code === 312 || result.code === 314) {
                        // 跳转重新登陆
                        // MessageBox.confirm('您已注销，可以取消以停留在此页面，或重新登录', '确认注销', {
                        //     confirmButtonText: '登陆',
                        //     cancelButtonText: '取消',
                        //     type: 'warning',
                        // }).then(() => {
                        //     // vuex 重置令牌
                        //     // store.dispatch('user/resetToken').then(() => {
                        //     //     location.reload();
                        //     // });
                        //     location.reload();
                        // });
                        // return Promise.reject(new Error(data.stack || 'Error'));
                    }
                    return Promise.reject(new Error('应用CMS平台Error状态码', result.code));
                }

                // 统计接口/时长/路由
                endTime = new Date().getTime()
                if(response){
                    store.dispatch('requestCont/addAsyncMethods',{  // 如果响应成功  记录当前请求
                        requestName: action,  // 请求路径
                        requestStatus: response.status,  // 请求代码  成功200  失败记录失败代码
                        errMsg: response.statusText,  //  请求错误信息
                        time: endTime-startTime  //  请求时间
                    });
                }

                // 数据正常返回
                return result;
            },
            error => {
                // 统计接口/时长/路由
                endTime = new Date().getTime()
                store.dispatch('requestCont/addAsyncMethods',{  // 如果响应成功  记录当前请求
                    requestName: action,  // 请求路径
                    requestStatus: error && error.response && error.response.status && error.response.status,  // 请求代码  成功200  失败记录失败代码
                    printMsg: error && error.response && error.response.statusText && error.response.statusText,  //  请求错误信息
                    time: endTime - startTime  //  请求时间
                });

                Message({
                    message: error.message,
                    type: 'error',
                    duration: messageDuration,
                });
                return Promise.reject(error);
            },
        );
    }

    async request(options) {
        /**
         * 1、使用某一种规则包装此接口配置单
         * 2、baseUrl，url，method，dataType，authPermission，prop包含的属性
         * 3、baseUrl：由swagger提供公用字符串
         * 4、url：由swagger提供排除baseUrl剩下的字符串
         * 5、method：由swagger提供，转换统一大写
         * 6、dataType：由swagger里请求content-type决定，或其它方式
         * 7、authPermission：由swagger的http请求头，或其它方式
         * 8、prop入参规则，由async-validator规则编写
         */
        const instance = axios.create({
            // `timeout` 指定请求超时的毫秒数(0 表示无超时时间)
            // 如果请求话费了超过 `timeout` 的时间，请求将被中断
            // 默认: 5000
            timeout: requestTimeout,
        });
        // axios属性配置
        const _options = await this.setConfig(options);
        // 属性及校验配置
        const optionsAndProp = merge(options, _options);
        this.interceptors(instance, optionsAndProp);
        // 返回
        return instance(optionsAndProp);
    }
}

export function request({ ...options }) {
    return new BasicRequest().request(options);
}

// 对外开放request的调用，main.js增加Vue.use(Request, {……})
export function install(Vue, { ...options } = {}) {
    const create = new BasicRequest().request(options);
    Vue.prototype.$ce.request = Vue.ce.request = create;
}
